import React, { useState, useMemo } from 'react';
import {
    Button, Divider, Input, Modal, Select, Upload, Typography, Tooltip, message, AutoComplete, Switch, Grid
} from 'antd';
import { IoCloudUploadOutline, IoPricetagsOutline } from "react-icons/io5";
import { QuestionCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import { UNIDADES_MEDIDA, OPCIONES_PRESENTACION, obtenerListasDinamicas } from '../dataProductos';
import { useSaveProveedor } from '../../../hooks/useProveedores';
import SeccionVariantesModal from './SeccionVariantesModal';
import SeccionCombosModal from './SeccionCombosModal';
const { Text } = Typography;
const { useBreakpoint } = Grid;

const FormularioProductos = ({
    productosDB, producto, saveMutation, setOpen, open, setProducto, estadoInicialProducto,
    setMargenGanancia, calcularMargenDesdePrecio, margenGanancia, calcularPrecioDesdeMargen, setTargetPrecios,
    listasPrecios, setTempPrecios, setOpenPrecios, proveedoresDB
}) => {

    const screens = useBreakpoint();
    const isMobile = screens.md === false;

    const saveProveedorMutation = useSaveProveedor();

    const { listaCategorias, ubicacionesSugeridas } = obtenerListasDinamicas(productosDB);
    const [tempInputs, setTempInputs] = useState({ cat: '', ubi: '', prov: '' });
    const [errorCodigoBarras, setErrorCodigoBarras] = useState(null);
    const [errorCodigoInterno, setErrorCodigoInterno] = useState(null);
    const [intentaronGuardar, setIntentaronGuardar] = useState(false);

    const productoGuardado = producto.id ? productosDB.find(p => p.id === producto.id) : null;
    const tieneSkuOriginal = productoGuardado ? !!productoGuardado.codigo_interno : false;
    const tieneBarrasOriginal = productoGuardado ? !!productoGuardado.codigo_barras : false;

    const guardarProductos = async () => {

        setIntentaronGuardar(true);

        if (producto.has_variants && (!producto.variantes || producto.variantes.length === 0)) {
            return message.warning("Debes agregar al menos una presentación (ej. Talla, Color).");
        }

        if (!producto.nombre || String(producto.nombre).trim() === "") return message.warning("El nombre del producto es obligatorio.");
        if (!producto.has_variants && (!producto.codigoBarras || String(producto.codigoBarras).trim() === "") && (!producto.codigoInterno || String(producto.codigoInterno).trim() === "")) {
            message.warning("Debes asignar un Código de Barras o un Código Interno.");
            return;
        }
        if (!producto.descripcion || String(producto.descripcion).trim() === "") return message.warning("La descripción es obligatoria.");
        if (!producto.categoria || String(producto.categoria).trim() === "") return message.warning("Seleccione una categoría.");
        if (!producto.has_variants && (!producto.precioVenta || Number(producto.precioVenta) <= 0)) return message.warning("El precio de venta es obligatorio.");
        if (!producto.has_variants && !producto.es_combo) {
            if (producto.precioCompra === "" || producto.precioCompra === null) {
                return message.warning("El costo de compra es obligatorio. Si no tiene costo, ingresa 0.");
            }
            if (producto.stock === "" || producto.stock === null) {
                return message.warning("Debes ingresar el stock inicial del producto.");
            }
        }
        if (!producto.marca || String(producto.marca).trim() === "") return message.warning("La marca es obligatoria.");
        if (!producto.proveedor_id) return message.warning("El proveedor es obligatorio.");

        if (!producto.has_variants) {
            if (producto.codigoBarras && String(producto.codigoBarras).trim() !== "") {
                const codigoExiste = productosDB.find(p => String(p.codigo_barras) === String(producto.codigoBarras) && p.id !== producto.id);
                if (codigoExiste) {
                    setErrorCodigoBarras(`Este código ya es usado por: ${codigoExiste.nombre}`);
                    return;
                }
            }
            if (producto.codigoInterno && String(producto.codigoInterno).trim() !== "") {
                const codigoInternoExiste = productosDB.find(p => String(p.codigo_interno) === String(producto.codigoInterno) && p.id !== producto.id);
                if (codigoInternoExiste) {
                    setErrorCodigoInterno(`Este SKU ya es usado por: ${codigoInternoExiste.nombre}`);
                    return;
                }
            }
        }

        // VALIDACIÓN DE DUPLICADOS DENTRO DE LAS VARIANTES
        if (producto.has_variants) {
            const nombresVazios = producto.variantes.some(v => !v.atributos.nombre || String(v.atributos.nombre).trim() === "");
            if (nombresVazios) return message.warning("Todas las presentaciones deben tener un nombre (Ej: Talla M).");

            const codigosBarras = producto.variantes.map(v => v.codigo_barras).filter(c => c && String(c).trim() !== "");
            if (new Set(codigosBarras).size !== codigosBarras.length) {
                return message.warning("Error: Tienes códigos de barras repetidos en la tabla de presentaciones.");
            }

            const skus = producto.variantes.map(v => v.codigo_interno).filter(c => c && String(c).trim() !== "");
            if (new Set(skus).size !== skus.length) {
                return message.warning("Error: Tienes Códigos Internos (SKU) repetidos en la tabla de presentaciones.");
            }
        }

        if (producto.es_combo && (!producto.comboItems || producto.comboItems.length == 0 )) {
            return message.warning("Debes seleccionar al menos un item para el combo ");
        }

        if (producto.es_combo && (!producto.comboItems || (producto.comboItems.length == 1 && producto.comboItems[0].cantidad == 1 ))) {
            return message.warning("Un combo no debe ser un producto de una sola cantidad.");
        }

        const payload = {
            id: producto.id,
            nombre: producto.nombre,
            codigo_barras: producto.codigoBarras,
            codigo_interno: producto.codigoInterno,
            descripcion: producto.descripcion,
            categoria: producto.categoria,
            unidad_medida: producto.unidadMedida,
            presentacion: producto.presentacion,
            marca: producto.marca,
            ubicacion: producto.ubicacion,
            precio_compra: Number(producto.precioCompra),
            precio_venta: Number(producto.precioVenta),
            precio_catalogo: Number(producto.precioCatalogo || producto.precioVenta),
            precios_adicionales: producto.preciosAdicionales || {},
            iva: Number(producto.impuesto),
            stock: producto.has_variants
                ? producto.variantes.reduce((sum, v) => sum + (Number(v.stock) || 0), 0)
                : Number(producto.stock),
            proveedor_id: producto.proveedor_id ?? null,
            imagen_url: producto.image ?? null,
            status: producto.status,
            descuento: Number(producto.descuento || 0),
            visible_catalogo: producto.visibleCatalogo,
            has_variants: producto.es_combo ? false : producto.has_variants,
            variantes: (producto.has_variants && !producto.es_combo) ? producto.variantes : [],
            es_combo: producto.es_combo,
            combo_items: producto.es_combo ? producto.comboItems : []
        };

        try {
            await saveMutation.mutateAsync(payload);
            setOpen(false);
            setProducto(estadoInicialProducto);
            setMargenGanancia("");
            setIntentaronGuardar(false);
        } catch (error) {
            if (error.response && error.response.status === 400) {
                message.error(error.response.data.detalle || "Error: El código de barras ya existe");
            } else {
                message.error("Ocurrió un error al guardar el producto.");
            }
        }
    };

    const cerrarModal = () => {
        setOpen(false);
        setProducto({ ...estadoInicialProducto });
        setMargenGanancia("");
        setErrorCodigoBarras(null);
        setErrorCodigoInterno(null);
        setIntentaronGuardar(false);
    };

    const abrirModalPrecios = (target) => {
        setTargetPrecios(target);

        const datosCrudos = target === 'simple'
            ? (producto.preciosAdicionales || {})
            : (producto.variantes[target]?.precios_adicionales || {});

        const datosNormalizados = {};
        listasPrecios.forEach(lista => {
            datosNormalizados[lista.id] = 0;
        });

        Object.entries(datosCrudos).forEach(([key, value]) => {
            if (datosNormalizados.hasOwnProperty(key)) {
                datosNormalizados[key] = Number(value) || 0;
            }
        });

        setTempPrecios(datosNormalizados);
        setOpenPrecios(true);
    };

    const formatoCOP = new Intl.NumberFormat('es-CO', {
        style: 'currency', currency: 'COP', minimumFractionDigits: 0,
    });

    const sugerenciasNombre = useMemo(() => {
        if (producto.id || !producto.nombre || producto.nombre.length < 2) return [];

        const lowerSearch = producto.nombre.toLowerCase();

        return productosDB
            .filter(p => p.nombre?.toLowerCase().includes(lowerSearch))
            .slice(0, 3)
            .map(p => ({
                value: p.nombre,
                label: (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <Text strong>{p.nombre}</Text>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            Copiar plantilla • Costo: {formatoCOP.format(p.precio_compra)}
                        </Text>
                    </div>
                ),
                datosCompletos: p
            }));
    }, [producto.nombre, productosDB, producto.id]);

    const calcularPrecioAlPublico = (precio, dcto) => {
        const p = Number(precio) || 0;
        const d = Number(dcto) || 0;
        return Math.round(p * (1 - (d / 100)));
    };

    const contarListasActivas = (datos) => {
        if (!datos || typeof datos !== 'object') return 0;

        return listasPrecios.filter(lista => {
            return Number(datos[lista.id]) > 0;
        }).length;
    };

    const generarEAN13Unico = (productosDB, variantesActuales = []) => {
        let esUnico = false;
        let codigoFinal = '';
    
        while (!esUnico) {
            let code = '20'; 
            for(let i=0; i<10; i++) code += Math.floor(Math.random() * 10).toString();
            
            let sum = 0;
            for (let i = 0; i < 12; i++) {
                sum += parseInt(code.charAt(i)) * (i % 2 === 0 ? 1 : 3);
            }
            const checkDigit = (10 - (sum % 10)) % 10;
            const candidato = code + checkDigit.toString();
    
            // Validar contra BD
            const existeBD = productosDB.some(p => 
                String(p.codigo_barras) === candidato || String(p.codigo_interno) === candidato
            );
    
            // Validar contra variantes creadas en el momento (estado local)
            const existeVariantes = variantesActuales.some(v => 
                String(v.codigo_barras) === candidato || String(v.codigo_interno) === candidato
            );
    
            if (!existeBD && !existeVariantes) {
                codigoFinal = candidato;
                esUnico = true;
            }
        }
        return codigoFinal;
    };

    return (
        <Modal
            destroyOnClose={true}
            title={
                <span style={{ fontSize: '24px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {producto.id ? "Editar Producto" : "Crear Producto"}
                </span>}
            centered
            open={open}
            onCancel={cerrarModal}
            afterClose={() => { setProducto(estadoInicialProducto); setMargenGanancia(""); }}
            width={isMobile ? '100%' : 1000}
            cancelText="CANCELAR"
            cancelButtonProps={{ style: { height: '45px', borderRadius: '8px', padding: '0 25px', fontWeight: '600' } }}
            okText="GUARDAR PRODUCTO"
            okButtonProps={{
                className: "btnGuardar",
                disabled: errorCodigoBarras !== null || errorCodigoInterno !== null,
                style: { height: '45px', borderRadius: '8px', padding: '0 25px', fontSize: '15px', fontWeight: 'bold' }
            }}
            onOk={guardarProductos}
            confirmLoading={saveMutation.isPending}
            styles={{ body: { maxHeight: 'calc(100vh - 200px)', overflowY: 'auto', overflowX: 'hidden', padding: '0px 2px' } }}
        >
            <div className="modal-yugonub" style={{ display: "flex", flexDirection: "column", gap: "25px", padding: isMobile ? '10px 0' : '5px 15px' }}>

                {/* --- SECCIÓN: GENERAL (IMAGEN, NOMBRE, DESCRIPCIÓN, CÓDIGOS) --- */}
                <section>
                    <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#1890ff', borderBottom: '1px solid #e8e8e8', marginBottom: '8px', paddingBottom: '5px' }}>
                        Información General
                    </h3>

                    <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start', flexDirection: isMobile ? 'column' : 'row' }}>
                        <div style={{
                            width: isMobile ? '100%' : '220px',
                            height: '220px',
                            flexShrink: 0,
                            border: '2px dashed #cbd5e1',
                            borderRadius: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#f8fafc',
                            position: 'relative'
                        }}>
                            {producto.image ? (
                                <>
                                    <img src={producto.image} alt="Preview" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: '10px' }} />
                                    <Button type="primary" danger shape="circle" icon={<DeleteOutlined />} size="small" style={{ position: 'absolute', top: -5, right: -5 }} onClick={() => setProducto({ ...producto, image: "" })} />
                                </>
                            ) : (
                                <Upload
                                    listType="picture"
                                    showUploadList={false}
                                    beforeUpload={(file) => {
                                        const reader = new FileReader();
                                        reader.onload = (e) => setProducto({ ...producto, image: e.target.result });
                                        reader.readAsDataURL(file);
                                        return false;
                                    }}
                                >
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: '#64748b', cursor: 'pointer' }}>
                                        <IoCloudUploadOutline size={32} style={{ color: '#22BABB', marginBottom: '8px' }} />
                                        <span style={{ fontSize: '12px', textAlign: 'center', fontWeight: '600' }}>Subir Imagen</span>
                                    </div>
                                </Upload>
                            )}
                        </div>

                        {/* Nombre a la Derecha */}
                        <div style={{ flex: 1, width: '100%' }}>
                            <label>Nombre del producto <span style={{ color: '#ff4d4f', fontSize: '15px' }}>*</span></label>
                            <AutoComplete
                                style={{ width: '100%', height: '45px' }}
                                options={sugerenciasNombre}
                                status={intentaronGuardar && (!producto.nombre || String(producto.nombre).trim() === "") ? "error" : ""}
                                value={producto.nombre}
                                onChange={(valor) => setProducto({ ...producto, nombre: valor })}
                                onSelect={(valor, option) => {
                                    const p = option.datosCompletos;
                                    setProducto({
                                        ...producto,
                                        nombre: valor,
                                        descripcion: p.descripcion || "",
                                        categoria: p.categoria || "",
                                        precioCompra: p.precio_compra || "",
                                        precioVenta: p.precio_venta || "",
                                        impuesto: p.iva || 0,
                                        unidadMedida: p.unidad_medida || "",
                                        presentacion: p.presentacion || "",
                                        marca: p.marca || "",
                                        proveedor_id: p.proveedor_id ? { nombre: p.proveedor_id } : null,
                                        ubicacion: p.ubicacion || "",
                                        codigoInterno: "",
                                        descuento: p.descuento || 0,
                                        codigoBarras: "",
                                        stock: 0,
                                        image: "",
                                        precioCatalogo: p.precio_catalogo || p.precio_venta || ""
                                    });

                                    const margen = calcularMargenDesdePrecio(p.precio_compra, p.precio_venta);
                                    setMargenGanancia(margen);

                                    message.success("Datos copiados. Cambia lo necesario y escanea el nuevo código.");
                                }}
                            >
                                <Input style={{ width: '100%', height: '45px', fontSize: '17px', fontWeight: '600' }} placeholder="¿Qué vas a vender?" status={intentaronGuardar && (!producto.nombre || String(producto.nombre).trim() === "") ? "error" : ""} allowClear />
                            </AutoComplete>

                            <div style={{ marginTop: "8px" }}>
                                <label>Descripción detallada <span style={{ color: '#ff4d4f', fontSize: '15px' }}>*</span></label>
                                <Input.TextArea
                                    style={{ height: '45px', fontSize: '17px', fontWeight: '600', padding: '3px' }}
                                    placeholder="Ingredientes, peso, sabor, características..."
                                    autoSize={{ minRows: 1, maxRows: 6 }}
                                    value={producto.descripcion}
                                    status={intentaronGuardar && (!producto.descripcion || String(producto.descripcion).trim() === "") ? "error" : ""}
                                    onChange={(e) => setProducto({ ...producto, descripcion: e.target.value })}
                                    allowClear
                                />
                            </div>

                            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "15px", marginTop: "8px" }}>
                                <div>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        Código de barras general
                                        {tieneBarrasOriginal && !producto.has_variants && <Tooltip title="El código de barras de un producto o variante ya creado no se puede modificar por temas de trazabilidad." color="#1890ff">
                                            <QuestionCircleOutlined style={{ color: '#FF7875', cursor: 'pointer' }} />
                                        </Tooltip>}
                                    </label>
                                    <Input
                                        style={{ height: '45px', fontSize: '17px', fontWeight: '600', backgroundColor: producto.has_variants ? '#f1f5f9' : '#fff' }}
                                        placeholder={producto.has_variants ? "Inhabilitado (Ingrésalo en la variante)" : "Escanea el código"}
                                        value={producto.has_variants ? "" : producto.codigoBarras}
                                        status={!producto.has_variants && errorCodigoBarras ? "error" : (!producto.has_variants && intentaronGuardar && !producto.codigoBarras && !producto.codigoInterno ? "error" : "")}
                                        allowClear
                                        disabled={(tieneBarrasOriginal && !!producto.id) || producto.has_variants}
                                        title={tieneBarrasOriginal ? "El código de barras ya está asignado y no se puede modificar." : "Puedes escanear o escribir un código de barras."}
                                        onChange={(e) => {
                                            const nuevoCodigo = e.target.value;
                                            setProducto({ ...producto, codigoBarras: nuevoCodigo });

                                            if (nuevoCodigo && String(nuevoCodigo).trim() !== "") {
                                                const codigoExiste = productosDB.find(p => String(p.codigo_barras) === String(nuevoCodigo) && p.id !== producto.id);
                                                setErrorCodigoBarras(codigoExiste ? `Este código ya es usado por: ${codigoExiste.nombre}` : null);
                                            } else {
                                                setErrorCodigoBarras(null);
                                            }
                                        }}
                                        suffix={
                                            !producto.has_variants && (!tieneBarrasOriginal || !producto.id) ? (
                                                <Tooltip title="Generar código de barras interno (EAN-13)">
                                                    <Button 
                                                        type="text" 
                                                        icon={<IoPricetagsOutline size={18} />} 
                                                        style={{ background: '#22BABB', borderColor: '#22BABB', boxShadow: 'none' }}
                                                        onClick={() => {
                                                            const nuevoCodigo = generarEAN13Unico(productosDB, []);
                                                            setProducto({ ...producto, codigoBarras: nuevoCodigo });
                                                            setErrorCodigoBarras(null);
                                                            message.success("Código único generado.");
                                                        }}
                                                    />
                                                </Tooltip>
                                            ) : null
                                        }
                                    />
                                    {!producto.has_variants && errorCodigoBarras && <small style={{ color: '#ff4d4f', display: 'block', marginTop: '4px', lineHeight: '1.2' }}>{errorCodigoBarras}</small>}
                                </div>

                                <div>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        Código Interno / SKU
                                        {tieneSkuOriginal && <Tooltip title="El SKU de un producto ya creado no se puede modificar por temas de trazabilidad." color="#1890ff">
                                            <QuestionCircleOutlined style={{ color: '#FF7875', cursor: 'pointer' }} />
                                        </Tooltip>}
                                    </label>
                                    <Input
                                        style={{ height: '45px', fontSize: '17px', fontWeight: '600', backgroundColor: producto.has_variants ? '#f1f5f9' : '#fff' }}
                                        placeholder={producto.has_variants ? "Inhabilitado (Ingrésalo en la variante)" : "Ej. 001 (Opcional)"}
                                        value={producto.has_variants ? "" : producto.codigoInterno}
                                        status={!producto.has_variants && errorCodigoInterno ? "error" : (!producto.has_variants && intentaronGuardar && !producto.codigoBarras && !producto.codigoInterno ? "error" : "")}
                                        allowClear
                                        disabled={(tieneSkuOriginal && !!producto.id) || producto.has_variants}
                                        title={tieneSkuOriginal ? "El SKU ya está asignado y no se puede modificar." : "Puedes asignar un SKU si lo deseas."}
                                        onChange={(e) => {
                                            const nuevoCodigo = e.target.value;
                                            setProducto({ ...producto, codigoInterno: nuevoCodigo });

                                            if (nuevoCodigo && String(nuevoCodigo).trim() !== "") {
                                                const codigoExiste = productosDB.find(p => String(p.codigo_interno) === String(nuevoCodigo) && p.id !== producto.id);
                                                setErrorCodigoInterno(codigoExiste ? `Este SKU ya es usado por: ${codigoExiste.nombre}` : null);
                                            } else {
                                                setErrorCodigoInterno(null);
                                            }
                                        }}
                                    />
                                    {!producto.has_variants && errorCodigoInterno && <small style={{ color: '#ff4d4f', display: 'block', marginTop: '4px', lineHeight: '1.2' }}>{errorCodigoInterno}</small>}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* --- SECCIÓN: FINANZAS E INVENTARIO --- */}
                <section>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e8e8e8', marginBottom: '15px', paddingBottom: '8px' }}>
                        <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#1890ff', margin: 0 }}>
                            Finanzas e Inventario
                        </h3>

                        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                            {/* SWITCH VARIANTES */}
                            {!producto.es_combo && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span style={{ fontSize: '14px', fontWeight: '600', color: producto.has_variants ? '#0958d9' : '#64748b' }}>
                                        ¿Este producto tiene variantes?
                                    </span>
                                    <Switch
                                        checked={producto.has_variants}
                                        style={{ backgroundColor: producto.has_variants ? '#1890ff' : '#94a3b8' }}
                                        onChange={(checked) => {
                                            if (checked) {
                                                const activarVariantes = () => {
                                                    const varianteBase = {
                                                        id: null,
                                                        codigo_barras: producto.codigoBarras || "",
                                                        codigo_interno: producto.codigoInterno || "",
                                                        atributos: { nombre: "" },
                                                        stock: producto.stock || 0,
                                                        precio_compra: producto.precioCompra || 0,
                                                        precio_base: producto.precioVenta || 0,
                                                        precio_catalogo: producto.precioCatalogo || producto.precioVenta || 0,
                                                        descuento: producto.descuento || 0,
                                                        status: 'active'
                                                    };
                                                    setProducto({ ...producto, has_variants: true, variantes: [varianteBase], es_combo: false });
                                                };

                                                if (producto.stock > 0) {
                                                    Modal.confirm({
                                                        title: <span style={{ fontSize: '18px', fontWeight: 'bold' }}>¿Activar presentaciones múltiples?</span>,
                                                        content: `Actualmente tienes ${producto.stock} unidades. Al activar esta opción, deberás distribuir el stock exacto entre tus nuevas presentaciones. ¿Continuar?`,
                                                        okText: 'Sí, activar',
                                                        cancelText: 'Cancelar',
                                                        centered: true,
                                                        cancelButtonProps: { style: { height: '40px', borderRadius: '8px', padding: '0 20px', fontWeight: '600' } },
                                                        okButtonProps: { style: { height: '40px', borderRadius: '8px', padding: '0 20px', fontWeight: 'bold', backgroundColor: '#22BABB', borderColor: '#22BABB', color: '#fff' } },
                                                        onOk: activarVariantes
                                                    });
                                                } else {
                                                    activarVariantes();
                                                }
                                            } else if (!checked && producto.variantes.length > 0) {
                                                Modal.confirm({
                                                    title: <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#ff4d4f' }}>¿Desactivar presentaciones múltiples?</span>,
                                                    content: 'El sistema sumará el inventario de todas las presentaciones y lo convertirá en un solo stock general. Las variantes se ocultarán. ¿Estás seguro?',
                                                    okText: 'Sí, desactivar',
                                                    cancelText: 'Cancelar',
                                                    centered: true,
                                                    cancelButtonProps: { style: { height: '40px', borderRadius: '8px', padding: '0 20px', fontWeight: '600' } },
                                                    okButtonProps: { danger: true, style: { height: '40px', borderRadius: '8px', padding: '0 20px', fontWeight: 'bold' } },
                                                    onOk: () => {
                                                        const stockAcumulado = producto.variantes.reduce((acum, v) => acum + Number(v.stock || 0), 0);
                                                        setProducto({ ...producto, has_variants: false, stock: stockAcumulado });
                                                    }
                                                });
                                            } else {
                                                setProducto({ ...producto, has_variants: false });
                                            }
                                        }}
                                    />
                                </div>
                            )}

                            {/* SWITCH COMBO */}
                            {!producto.has_variants && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: producto.es_combo ? '#f6ffed' : 'transparent', padding: '4px 12px', borderRadius: '8px', border: producto.es_combo ? '1px solid #b7eb8f' : '1px solid transparent' }}>
                                    <span style={{ fontSize: '14px', fontWeight: 'bold', color: producto.es_combo ? '#389e0d' : '#64748b' }}>¿Es un Combo?</span>
                                    <Switch
                                        checked={producto.es_combo}
                                        style={{ backgroundColor: producto.es_combo ? '#52c41a' : '#94a3b8' }}
                                        onChange={(checked) => {
                                            if (checked) {
                                                // Si lo enciende, lo convertimos en combo
                                                setProducto({ ...producto, es_combo: true, has_variants: false, stock: 0 });
                                            } else {
                                                // Si lo apaga, lanzamos la alerta
                                                Modal.confirm({
                                                    title: <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#ff4d4f' }}>¿Desactivar modo Combo?</span>,
                                                    content: 'Al desactivar el combo, los ingredientes se borrarán. Deberás ingresar el Costo de Compra y el Stock inicial de forma manual. ¿Deseas continuar?',
                                                    okText: 'Sí, desactivar',
                                                    cancelText: 'Cancelar',
                                                    centered: true,
                                                    cancelButtonProps: { style: { height: '40px', borderRadius: '8px', padding: '0 20px', fontWeight: '600' } },
                                                    okButtonProps: { danger: true, style: { height: '40px', borderRadius: '8px', padding: '0 20px', fontWeight: 'bold' } },
                                                    onOk: () => {
                                                        // Vaciamos el costo y dejamos stock en 0 para obligar al usuario a llenarlos
                                                        setProducto({ 
                                                            ...producto, 
                                                            es_combo: false, 
                                                            has_variants: false, 
                                                            stock: 0, 
                                                            precioCompra: "", 
                                                            comboItems: [] 
                                                        });
                                                        setMargenGanancia(""); // Reiniciamos el margen porque borramos el costo
                                                    }
                                                });
                                            }
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* === INTERFAZ DEL COMBO === */}
                    {producto.es_combo && (
                        <SeccionCombosModal
                            productosDB={productosDB}
                            producto={producto}
                            calcularMargenDesdePrecio={calcularMargenDesdePrecio}
                            setMargenGanancia={setMargenGanancia}
                            setProducto={setProducto}
                        />
                    )}

                    {/* IVA GLOBAL CUANDO HAY VARIANTES (Arriba de la tabla) */}
                    {producto.has_variants && (
                        <div style={{ marginBottom: '15px', width: isMobile ? '100%' : '200px' }}>
                            <label>IVA (%) Global</label>
                            <Input
                                style={{ height: '45px', fontSize: '17px', fontWeight: '600' }}
                                type="number" suffix="%" value={producto.impuesto}
                                onChange={(e) => setProducto({ ...producto, impuesto: e.target.value })}
                                onFocus={(e) => e.target.select()}
                            />
                        </div>
                    )}

                    {/* CUADRÍCULA  */}
                    {!producto.has_variants && (
                        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 1fr 1fr 1fr", gap: "15px" }}>
                            <div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    Costo Compra
                                    {producto.id && !producto.es_combo && <Tooltip title="Para cambiar el costo usa el módulo de inventario." color="#1890ff"><QuestionCircleOutlined style={{ color: '#FF7875', cursor: 'pointer' }} /></Tooltip>}
                                    {producto.es_combo && <Tooltip title="El costo del combo se calcula automáticamente sumando el costo de los productos que lo componen." color="#1890ff"><QuestionCircleOutlined style={{ color: '#1890ff', cursor: 'pointer' }} /></Tooltip>}
                                </label>
                                <Input
                                    style={{ height: '45px', fontSize: '17px', fontWeight: '600', backgroundColor: producto.es_combo ? '#f1f5f9' : '#fff' }}
                                    prefix="$"
                                    placeholder="0"
                                    value={producto.precioCompra ? new Intl.NumberFormat('es-CO').format(producto.precioCompra) : (producto.precioCompra === 0 ? "0" : "")}
                                    disabled={!!producto.id || producto.es_combo}
                                    allowClear
                                    onChange={(e) => {
                                        const nuevoCosto = e.target.value.replace(/\./g, '').replace(/[^0-9]/g, '');
                                        const nuevoMargen = calcularMargenDesdePrecio(nuevoCosto, producto.precioVenta);
                                        setProducto({ ...producto, precioCompra: nuevoCosto });
                                        setMargenGanancia(nuevoMargen);
                                    }}
                                />
                            </div>

                            <div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>Margen (%)<Tooltip title="Usamos el Margen Bruto Comercial. Esto calcula qué porcentaje del precio final que paga el cliente será tu ganancia libre." color="#1890ff"><QuestionCircleOutlined style={{ color: '#1890ff', cursor: 'pointer' }} /></Tooltip></label>
                                <Input style={{ height: '45px', fontSize: '17px', fontWeight: '600', borderColor: margenGanancia > 0 ? '#1890ff' : '' }} type="number" suffix="%" placeholder="Ej. 30" value={margenGanancia}
                                    onChange={(e) => {
                                        const nuevoMargen = e.target.value;
                                        setMargenGanancia(nuevoMargen);
                                        const precioCalculado = calcularPrecioDesdeMargen(producto.precioCompra, nuevoMargen);
                                        if (precioCalculado !== "") setProducto({ ...producto, precioVenta: precioCalculado, precioCatalogo: precioCalculado });
                                    }}
                                />
                            </div>

                            <div>
                                <label>Precio Venta <span style={{ color: '#ff4d4f', fontSize: '15px' }}>*</span></label>
                                <Input style={{ height: '45px', fontSize: '17px', fontWeight: '600' }} prefix="$" placeholder="0" value={producto.precioVenta ? new Intl.NumberFormat('es-CO').format(producto.precioVenta) : ""} status={(intentaronGuardar && !producto.precioVenta) || (Number(producto.precioVenta) <= Number(producto.precioCompra) && producto.precioCompra > 0) ? "error" : ""} allowClear
                                    onChange={(e) => {
                                        const nuevoPrecio = e.target.value.replace(/\./g, '').replace(/[^0-9]/g, '');
                                        setProducto({ ...producto, precioVenta: nuevoPrecio, precioCatalogo: nuevoPrecio });
                                        const margenCalculado = calcularMargenDesdePrecio(producto.precioCompra, nuevoPrecio);
                                        setMargenGanancia(margenCalculado);
                                    }}
                                />
                                {Number(producto.precioVenta) <= Number(producto.precioCompra) && producto.precioCompra > 0 && <small style={{ color: '#ff4d4f', fontSize: '13px', fontWeight: '500' }}>Debe ser mayor al costo.</small>}
                            </div>

                            <div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>Precio Catálogo</label>
                                <Input style={{ height: '45px', fontSize: '17px', fontWeight: '600', borderColor: '#0ea5e9' }} prefix="$" placeholder="0" value={producto.precioCatalogo ? new Intl.NumberFormat('es-CO').format(producto.precioCatalogo) : ""} allowClear onChange={(e) => { const nuevoPrecio = e.target.value.replace(/\./g, '').replace(/[^0-9]/g, ''); setProducto({ ...producto, precioCatalogo: nuevoPrecio }); }} />
                            </div>

                            <div>
                                <label>Descuento (%)</label>
                                <Input
                                    style={{ height: '45px', fontSize: '17px', fontWeight: '600', borderColor: producto.descuento > 0 ? '#52c41a' : '' }}
                                    type="number"
                                    suffix="%"
                                    value={producto.descuento}
                                    placeholder="0"
                                    onFocus={(e) => e.target.select()}
                                    onChange={(e) => { const val = Math.min(100, Math.max(0, Number(e.target.value))); setProducto({ ...producto, descuento: val }); }}
                                />
                            </div>

                            <div>
                                <label>IVA (%) Global</label>
                                <Input
                                    style={{ height: '45px', fontSize: '17px', fontWeight: '600' }}
                                    type="number" suffix="%" value={producto.impuesto}
                                    onChange={(e) => setProducto({ ...producto, impuesto: e.target.value })}
                                    onFocus={(e) => e.target.select()}
                                />
                            </div>

                            <div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>Stock
                                    {producto.id && !producto.es_combo && <Tooltip title="Para cambiar el stock usa el módulo de inventario." color="#1890ff"><QuestionCircleOutlined style={{ color: '#FF7875', cursor: 'pointer' }} /></Tooltip>}
                                    {producto.es_combo && <Tooltip title="El stock de un combo se calcula automáticamente en el punto de venta según la disponibilidad de sus ingredientes." color="#1890ff"><QuestionCircleOutlined style={{ color: '#1890ff', cursor: 'pointer' }} /></Tooltip>}
                                </label>
                                <Input
                                    style={{ height: '45px', fontSize: '17px', fontWeight: '600', backgroundColor: producto.es_combo ? '#f1f5f9' : '#fff' }}
                                    type={producto.es_combo ? "text" : "number"}
                                    value={producto.es_combo ? "Dinámico" : producto.stock}
                                    disabled={!!producto.id || producto.es_combo}
                                    onChange={(e) => setProducto({ ...producto, stock: e.target.value })}
                                    onFocus={(e) => !producto.es_combo && e.target.select()}
                                />
                            </div>

                            <div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>Listas de Precios
                                    <Tooltip title="Una lista de precio es ese precio que se le da a un cliente en específico, el cual su valor puede ser menor al precio de venta establecido. Estas listas se usan para clientes Mayoristas, Distribuidores etc." color="#1890ff"><QuestionCircleOutlined style={{ color: '#1890ff', cursor: 'pointer' }} /></Tooltip>
                                </label>
                                <Button
                                    type="primary"
                                    icon={<IoPricetagsOutline size={18} />}
                                    style={{ height: '45px', width: '100%', fontSize: '15px', fontWeight: 'bold', backgroundColor: '#0ea5e9', borderColor: '#0ea5e9', boxShadow: '0 4px 6px -1px rgba(15, 118, 110, 0.3)' }}
                                    onClick={() => abrirModalPrecios('simple')}
                                >
                                    Configurar ({contarListasActivas(producto.preciosAdicionales)})
                                </Button>
                            </div>

                            {producto.descuento > 0 && producto.precioVenta && (
                                <div
                                    style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '8px', padding: '0 12px', height: '45px', marginTop: '24px' }}
                                    title='El precio al público es con base en el precio de venta y el descuento aplicado.'
                                >
                                    <Text type="secondary" style={{ fontSize: '11px', color: '#52c41a', fontWeight: '600', lineHeight: '1' }}>Precio al Público</Text>
                                    <Text strong style={{ color: '#389e0d', fontSize: '16px', lineHeight: '1.2' }}>${calcularPrecioAlPublico(producto.precioVenta, producto.descuento).toLocaleString('es-CO')}</Text>
                                </div>
                            )}
                        </div>
                    )}

                    {/* --- INTERFAZ VARIANTES --- */}
                    {producto.has_variants && (
                        <SeccionVariantesModal
                            producto={producto}
                            productosDB={productosDB}
                            setProducto={setProducto}
                            productoGuardado={productoGuardado}
                            calcularMargenDesdePrecio={calcularMargenDesdePrecio}
                            calcularPrecioAlPublico={calcularPrecioAlPublico}
                            calcularPrecioDesdeMargen={calcularPrecioDesdeMargen}
                            abrirModalPrecios={abrirModalPrecios}
                        />
                    )}
                </section>

                {/* --- SECCIÓN: LOGÍSTICA Y CLASIFICACIÓN --- */}
                <section>
                    <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#1890ff', borderBottom: '1px solid #e8e8e8', marginBottom: '15px', paddingBottom: '5px' }}>
                        Clasificación y Logística
                    </h3>
                    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: "15px" }}>

                        <div>
                            <label>Categoría <span style={{ color: '#ff4d4f', fontSize: '15px' }}>*</span></label>
                            <Select
                                style={{ width: '100%', height: '45px', fontSize: '15px' }}
                                placeholder="Seleccionar..."
                                status={intentaronGuardar && (!producto.categoria || String(producto.categoria).trim() === "") ? "error" : ""}
                                value={producto.categoria || undefined}
                                onChange={(valor) => setProducto({ ...producto, categoria: valor })}
                                dropdownRender={(menu) => (
                                    <>
                                        {menu}
                                        <Divider style={{ margin: '8px 0' }} />
                                        <div style={{ display: 'flex', padding: '4px 8px', gap: '8px' }}>
                                            <Input style={{ fontSize: '17px', fontWeight: '600' }} placeholder="Nueva..." value={tempInputs.cat} onChange={(e) => setTempInputs({ ...tempInputs, cat: e.target.value })} onKeyDown={(e) => e.stopPropagation()} />
                                            <Button type="text" onClick={() => { if (tempInputs.cat.trim()) { setProducto({ ...producto, categoria: tempInputs.cat.trim() }); setTempInputs({ ...tempInputs, cat: '' }); } }}>Add</Button>
                                        </div>
                                    </>
                                )}
                                options={listaCategorias.map((item) => ({ label: item, value: item }))}
                            />
                        </div>

                        <div>
                            <label>Unidad de medida</label>
                            <Select
                                style={{ width: '100%', height: '45px' }}
                                placeholder="Ej. Kilogramos"
                                status={intentaronGuardar && (!producto.unidadMedida || String(producto.unidadMedida).trim() === "") ? "error" : ""}
                                value={producto.unidadMedida || undefined}
                                onChange={(valor) => setProducto({ ...producto, unidadMedida: valor })}
                                options={UNIDADES_MEDIDA}
                                showSearch
                            />
                        </div>

                        <div>
                            <label>Presentación </label>
                            <Select
                                style={{ width: '100%', height: '45px' }}
                                placeholder="Ej. Botella"
                                status={intentaronGuardar && (!producto.presentacion || String(producto.presentacion).trim() === "") ? "error" : ""}
                                value={producto.presentacion ? [producto.presentacion] : []}
                                onChange={(valor) => setProducto({ ...producto, presentacion: valor })}
                                options={OPCIONES_PRESENTACION}
                                showSearch
                            />
                        </div>

                        <div>
                            <label>Marca <span style={{ color: '#ff4d4f', fontSize: '15px' }}>*</span></label>
                            <Input
                                style={{ height: '45px', fontSize: '16px', fontWeight: '500' }}
                                placeholder="Ej. Coca-Cola"
                                value={producto.marca}
                                status={intentaronGuardar && (!producto.marca || String(producto.marca).trim() === "") ? "error" : ""}
                                allowClear
                                onChange={(e) => setProducto({ ...producto, marca: e.target.value })}
                            />
                        </div>

                        <div>
                            <label>Ubicación</label>
                            <Select
                                style={{ width: '100%', height: '45px' }}
                                placeholder="Ej. Pasillo 3"
                                value={producto.ubicacion || undefined}
                                onChange={(valor) => setProducto({ ...producto, ubicacion: valor })}
                                showSearch
                                dropdownRender={(menu) => (
                                    <>
                                        {menu}
                                        <Divider style={{ margin: '8px 0' }} />
                                        <div style={{ display: 'flex', padding: '4px 8px', gap: '8px' }}>
                                            <Input placeholder="Nueva..." value={tempInputs.ubi} onChange={(e) => setTempInputs({ ...tempInputs, ubi: e.target.value })} onKeyDown={(e) => e.stopPropagation()} />
                                            <Button type="text" onClick={() => { if (tempInputs.ubi.trim()) { setProducto({ ...producto, ubicacion: tempInputs.ubi.trim() }); setTempInputs({ ...tempInputs, ubi: '' }); } }}>Add</Button>
                                        </div>
                                    </>
                                )}
                                options={[
                                    ...(producto.ubicacion && !ubicacionesSugeridas.includes(producto.ubicacion) ? [{ label: producto.ubicacion, value: producto.ubicacion }] : []),
                                    ...ubicacionesSugeridas.map(u => ({ label: u, value: u }))
                                ]}
                            />
                        </div>

                        <div>
                            <label>Proveedor <span style={{ color: '#ff4d4f', fontSize: '15px' }}>*</span></label>
                            <Select
                                showSearch
                                style={{ width: '100%', height: '45px' }}
                                placeholder="Buscar proveedor..."
                                status={intentaronGuardar && !producto.proveedor_id ? "error" : ""}
                                value={producto.proveedor_id || undefined}
                                onChange={(valor) => setProducto({ ...producto, proveedor_id: valor })}
                                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                                options={proveedoresDB.map(p => ({ label: p.nombre, value: p.id }))}
                                dropdownRender={(menu) => (
                                    <>
                                        {menu}
                                        <Divider style={{ margin: '8px 0' }} />
                                        <div style={{ display: 'flex', padding: '4px 8px', gap: '8px' }}>
                                            <Input style={{ fontSize: '16px', fontWeight: '500' }} placeholder="Nuevo proveedor..." value={tempInputs.prov} onChange={(e) => setTempInputs({ ...tempInputs, prov: e.target.value })} onKeyDown={(e) => e.stopPropagation()} />
                                            <Button type="text" loading={saveProveedorMutation.isPending} onClick={async () => {
                                                if (tempInputs.prov.trim()) {
                                                    try {
                                                        const nuevoProveedor = await saveProveedorMutation.mutateAsync({ nombre: tempInputs.prov.trim() });
                                                        setProducto({ ...producto, proveedor_id: nuevoProveedor.id });
                                                        setTempInputs({ ...tempInputs, prov: '' });
                                                        message.success("Proveedor creado exitosamente");
                                                    } catch (error) { message.error("Error al crear proveedor"); }
                                                }
                                            }}>Add</Button>
                                        </div>
                                    </>
                                )}
                            />
                        </div>
                    </div>
                </section>

                {/* --- SECCIÓN: ESTADO --- */}
                <section>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '15px' }}>
                        <div style={{ background: '#f8fafc', padding: '15px 20px', borderRadius: '12px', display: 'flex', alignItems: 'center', border: '1px solid #e2e8f0' }}>
                            <Switch
                                checked={producto.status === "active"}
                                onChange={(checked) => setProducto({ ...producto, status: checked ? "active" : "inactive" })}
                            />
                            <span style={{ marginLeft: '12px', fontWeight: '600', fontSize: '15px', color: producto.status === "active" ? '#16a34a' : '#94a3b8' }}>
                                {producto.status === "active" ? "Producto Activo (Visible en ventas)" : "Producto Inactivo (Oculto)"}
                            </span>
                        </div>
                        <div style={{ background: '#f0f9ff', padding: '15px 20px', borderRadius: '12px', display: 'flex', alignItems: 'center', border: '1px solid #bae6fd' }}>
                            <Switch
                                checked={producto.visibleCatalogo}
                                onChange={(checked) => setProducto({ ...producto, visibleCatalogo: checked })}
                            />
                            <span style={{ marginLeft: '12px', fontWeight: '600', fontSize: '15px', color: producto.visibleCatalogo ? '#0369a1' : '#64748b' }}>
                                {producto.visibleCatalogo ? "Producto Visible en Catálogo Online" : "Producto Oculto en Catálogo Online"}
                            </span>
                        </div>
                    </div>
                </section>
            </div>
        </Modal>
    )
}

export default FormularioProductos;